<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useUserInviteForm } from '@/composables/users/useUserInviteForm'

const { t } = useI18n()
const open = defineModel<boolean>('open', { default: false })
const emit = defineEmits<{ userInvited: [] }>()

const users = ref<Record<string, string>[]>([{ email: '', role: '' }])

const hasUnsavedChanges = computed(() => users.value.some((u) => u.email !== '' || u.role !== ''))

const resetForm = () => {
  users.value = [{ email: '', role: '' }]
}

const {
  localizedRoleOptions,
  showCancelConfirmation,
  rowErrors,
  isSubmitting,
  panelOpen,
  handleCancel,
  confirmDiscard,
  keepEditing,
  handleFieldBlur,
  handleSubmit,
} = useUserInviteForm({
  users,
  open,
  hasUnsavedChanges,
  onUserInvited: () => {
    emit('userInvited')
  },
  onReset: resetForm,
})
</script>

<template>
  <ORightPanel v-model:open="panelOpen" :title="t('create_users')">
    <template #default>
      <div class="create-user-panel flex h-full flex-col space-y-4 pb-2 text-neutral-1">
        <span class="typography-body-2-long">{{ t('users_import.description') }}</span>
        <OMultiInput
          v-model="users"
          :fields="['email', 'role']"
          :placeholders="[t('enter_team_member_email_address_placeholder'), t('role_placeholder')]"
          :field-types="['input', 'dropdown']"
          :field-input-types="['email']"
          :field-options="[[], localizedRoleOptions]"
          :row-errors="rowErrors"
          :add-label="t('users_import.add_new')"
          add-mode="progressive"
          :add-button-size="'S'"
          :field-defaults="['', '']"
          :delete-label="t('users_import.delete')"
          dataTestid="create-user-multi-input"
          class="min-h-0 flex-1"
          @fieldBlur="handleFieldBlur"
        />
      </div>
    </template>

    <template #footer>
      <OButton
        :label="t('cancel')"
        variant="secondary"
        color="neutral"
        size="L"
        @mousedown.prevent
        @click="handleCancel"
        dataTestid="create-user-cancel"
      />
      <OButton
        :label="t('save')"
        size="L"
        backgroundColor="success"
        :loading="isSubmitting"
        @click="handleSubmit"
        dataTestid="create-user-confirm"
      />
    </template>
  </ORightPanel>

  <OModal
    v-model:open="showCancelConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('continue')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>

<style scoped>
.create-user-panel :deep(.max-h-52) {
  max-height: none;
}

.create-user-panel :deep(.px-1) {
  padding-left: 0;
  overflow: visible;
}
</style>
